/**
 * Storage Settings Section
 * 
 * Displays storage overview and quick links for system admins.
 * Shown in the Settings page for users with admin role.
 */

import { Link } from 'react-router-dom';
import { Card, Icon, Skeleton } from '@/components/ui';
import { Progress } from '@/components/ui/progress';
import { usePlatformStats } from '../hooks';
import { HardDrive } from 'lucide-react';

export function StorageSettingsSection() {
  const { data: stats, isLoading, error } = usePlatformStats();

  if (error) {
    return null; // Silently fail for non-admins
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-gray-900 dark:text-gray-100">
          Platform Storage
        </h3>
        <Link
          to="/system/tenants"
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
        >
          Manage Tenants
          <Icon name="chevron-down" size={14} className="-rotate-90" />
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-2 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : stats ? (
        <div className="space-y-4">
          {/* Storage Progress */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                <HardDrive className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  Storage Usage
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  {stats.total_storage_used_gb.toFixed(1)} GB / {stats.total_storage_quota_gb} GB used
                </div>
                <Progress 
                  value={stats.storage_utilization_percent} 
                  className="h-2"
                />
              </div>
            </div>
            <span className={`text-sm font-medium ${
              stats.storage_utilization_percent > 90 
                ? 'text-red-600 dark:text-red-400' 
                : stats.storage_utilization_percent > 75 
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-green-600 dark:text-green-400'
            }`}>
              {stats.storage_utilization_percent.toFixed(0)}%
            </span>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 text-center pt-2">
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {stats.total_tenants}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Tenants</div>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {stats.total_documents.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Documents</div>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {stats.total_active_users_30d}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Active Users</div>
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
